import React, { useState, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  QrCode, Download, Copy, Share2, CheckCircle, FileText,
  ClipboardList, AlertTriangle, BookOpen, Printer
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Document types that can be shared via QR code
const DOCUMENT_TYPES = {
  ptp: { label: 'Pre-Task Plan', icon: ClipboardList, color: 'blue' },
  toolbox: { label: 'Toolbox Talk', icon: BookOpen, color: 'green' },
  jha: { label: 'Job Hazard Analysis', icon: AlertTriangle, color: 'orange' },
  incident: { label: 'Incident Report', icon: FileText, color: 'red' },
  permit: { label: 'Work Permit', icon: FileText, color: 'purple' },
};

// QR Code Generator Component
export const QRCodeGenerator = ({ documentType, documentId, documentTitle, onClose }) => {
  const [copied, setCopied] = useState(false);
  const qrRef = useRef(null);
  
  const shareUrl = `${window.location.origin}/shared/${documentType}/${documentId}`;
  const docConfig = DOCUMENT_TYPES[documentType] || { label: 'Document', icon: FileText, color: 'gray' };
  const Icon = docConfig.icon;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleDownloadQR = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = 300;
      canvas.height = 300;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, 300, 300);
      ctx.drawImage(img, 0, 0, 300, 300);
      
      const pngUrl = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `qr-${documentType}-${documentId}.png`;
      downloadLink.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const svg = qrRef.current?.querySelector('svg');
    if (!svg || !printWindow) return;

    const svgString = new XMLSerializer().serializeToString(svg);
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR Code - ${documentTitle}</title>
          <style>
            body {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 20px;
            }
            .qr-container {
              text-align: center;
              padding: 40px;
              border: 2px solid #e5e7eb;
              border-radius: 16px;
            }
            h2 { color: #1f2937; margin-bottom: 8px; }
            p { color: #6b7280; margin-bottom: 24px; font-size: 14px; }
            .url { 
              font-size: 12px; 
              color: #9ca3af; 
              word-break: break-all;
              max-width: 300px;
              margin-top: 16px;
            }
            @media print {
              body { padding: 0; }
              .qr-container { border: none; }
            }
          </style>
        </head>
        <body>
          <div class="qr-container">
            <h2>${docConfig.label}</h2>
            <p>${documentTitle}</p>
            ${svgString}
            <p class="url">${shareUrl}</p>
          </div>
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() { window.close(); };
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${docConfig.label}: ${documentTitle}`,
          text: `View this ${docConfig.label.toLowerCase()} on WSafe AI`,
          url: shareUrl,
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Share failed:', error);
        }
      }
    } else {
      handleCopyLink();
    }
  };

  return (
    <div className="space-y-6">
      {/* QR Code Display */}
      <div className="flex flex-col items-center">
        <div 
          ref={qrRef}
          className="bg-white p-6 rounded-2xl shadow-lg border-2 border-gray-100"
        >
          <QRCodeSVG
            value={shareUrl}
            size={200}
            level="H"
            includeMargin={true}
            imageSettings={{
              src: '/logo192.png',
              height: 40,
              width: 40,
              excavate: true,
            }}
          />
        </div>
        
        <div className="mt-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            <Icon className={`w-4 h-4 text-${docConfig.color}-600`} />
            <span className="text-sm font-medium text-gray-600">{docConfig.label}</span>
          </div>
          <p className="text-sm text-gray-500 max-w-xs truncate">{documentTitle}</p>
        </div>
      </div>

      {/* URL Display */}
      <div className="space-y-2">
        <Label className="text-sm text-gray-500">Share Link</Label>
        <div className="flex gap-2">
          <Input
            value={shareUrl}
            readOnly
            className="text-sm bg-gray-50 flex-1"
          />
          <Button
            variant="outline"
            size="icon"
            onClick={handleCopyLink}
            className="shrink-0"
          >
            {copied ? (
              <CheckCircle className="w-4 h-4 text-green-600" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-2 gap-3">
        <Button variant="outline" onClick={handleDownloadQR} className="gap-2">
          <Download className="w-4 h-4" />
          Download
        </Button>
        <Button variant="outline" onClick={handlePrint} className="gap-2">
          <Printer className="w-4 h-4" />
          Print
        </Button>
        <Button 
          onClick={handleShare} 
          className="col-span-2 bg-blue-600 hover:bg-blue-700 gap-2"
        >
          <Share2 className="w-4 h-4" />
          Share Document
        </Button>
      </div>
    </div>
  );
};

// QR Code Dialog Button - Use this in document pages
export const QRShareButton = ({ documentType, documentId, documentTitle, variant = 'outline', size = 'default' }) => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant={variant} size={size} className="gap-2" data-testid={`qr-share-btn-${documentId}`}>
          <QrCode className="w-4 h-4" />
          <span className="hidden sm:inline">QR Code</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="w-5 h-5 text-blue-600" />
            Share via QR Code
          </DialogTitle>
          <DialogDescription>
            Scan this QR code to access the document on any device
          </DialogDescription>
        </DialogHeader>
        <QRCodeGenerator
          documentType={documentType}
          documentId={documentId}
          documentTitle={documentTitle}
        />
      </DialogContent>
    </Dialog>
  );
};

// QR Code Scanner Component (for reading QR codes)
export const QRCodeScanner = ({ onScan, onError }) => {
  const [scanning, setScanning] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const startScanning = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      setScanning(true);
      
      // Note: For actual QR scanning, you'd need a library like jsQR
      // This is a placeholder for the camera view
    } catch (error) {
      console.error('Camera access denied:', error);
      onError?.('Camera access denied. Please allow camera permissions.');
    }
  };

  const stopScanning = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setScanning(false);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <QrCode className="w-5 h-5 text-blue-600" />
          Scan QR Code
        </CardTitle>
        <CardDescription>
          Point your camera at a QR code to access safety documents
        </CardDescription>
      </CardHeader>
      <CardContent>
        {scanning ? (
          <div className="space-y-4">
            <div className="relative aspect-square bg-slate-100 rounded-lg overflow-hidden">
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                playsInline
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-48 h-48 border-2 border-white rounded-lg" />
              </div>
              <canvas ref={canvasRef} className="hidden" />
            </div>
            <Button 
              variant="outline" 
              onClick={stopScanning} 
              className="w-full"
            >
              Stop Scanning
            </Button>
          </div>
        ) : (
          <Button 
            onClick={startScanning} 
            className="w-full bg-blue-600 hover:bg-blue-700 gap-2"
          >
            <QrCode className="w-4 h-4" />
            Start Camera
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

// Batch QR Code Generator for multiple documents
export const BatchQRGenerator = ({ documents }) => {
  const handlePrintAll = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const qrCodes = documents.map(doc => {
      const url = `${window.location.origin}/shared/${doc.type}/${doc.id}`;
      const config = DOCUMENT_TYPES[doc.type] || { label: 'Document' };
      return `
        <div class="qr-item">
          <h3>${config.label}</h3>
          <p>${doc.title}</p>
          <div id="qr-${doc.id}"></div>
          <p class="url">${url}</p>
        </div>
      `;
    }).join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR Codes - WSafe AI</title>
          <script src="https://cdn.jsdelivr.net/npm/qrcode@1.5.3/build/qrcode.min.js"></script>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
            }
            .qr-grid {
              display: grid;
              grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
              gap: 30px;
            }
            .qr-item {
              text-align: center;
              padding: 20px;
              border: 1px solid #e5e7eb;
              border-radius: 12px;
              page-break-inside: avoid;
            }
            h3 { color: #1f2937; margin-bottom: 4px; font-size: 14px; }
            p { color: #6b7280; margin-bottom: 12px; font-size: 12px; }
            .url { font-size: 10px; color: #9ca3af; word-break: break-all; margin-top: 12px; }
            canvas { display: block; margin: 0 auto; }
            @media print {
              .qr-item { break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <h1 style="text-align: center; margin-bottom: 30px;">Safety Document QR Codes</h1>
          <div class="qr-grid">${qrCodes}</div>
          <script>
            window.onload = function() {
              ${documents.map(doc => `
                QRCode.toCanvas(document.getElementById('qr-${doc.id}'), '${window.location.origin}/shared/${doc.type}/${doc.id}', { width: 150 });
              `).join('')}
              setTimeout(function() { window.print(); }, 500);
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Batch QR Codes</CardTitle>
        <CardDescription>
          Generate QR codes for {documents.length} documents
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="max-h-48 overflow-y-auto space-y-2">
            {documents.map(doc => {
              const config = DOCUMENT_TYPES[doc.type] || { label: 'Document', icon: FileText };
              const Icon = config.icon;
              return (
                <div 
                  key={doc.id}
                  className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg"
                >
                  <Icon className="w-4 h-4 text-gray-500" />
                  <span className="text-sm truncate flex-1">{doc.title}</span>
                </div>
              );
            })}
          </div>
          <Button 
            onClick={handlePrintAll}
            className="w-full bg-blue-600 hover:bg-blue-700 gap-2"
          >
            <Printer className="w-4 h-4" />
            Print All QR Codes
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default QRShareButton;
